import React, { FC, useEffect, useRef, useState } from 'react';
import { ButtonRadioGroup } from 'components/Buttons/ButtonRadioGroup/ButtonRadioGroup';
import { useAuth } from 'providers/useAuth/AuthContext';
import { useToasts } from 'providers/useToast/ToastProvider';
import Button from 'components/Buttons/Button/Button';
import { PageWrapper, Section } from 'components/Pages/Settings/PageSettingsCommon.css';
import translate from 'utils/fnTranslate';
import { useApp } from 'providers/useApp/AppContext';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getHelmetData } from 'utils/fnHelmet';
import { guardedLocalStorage } from 'utils/fnStorage';
import { PageHeading } from 'components/PageHeading/PageHeading.css';
import { ButtonWrapper, Hint, SectionTitle, SubSection, SubtitlePreview, SubtitlePreviewText } from './PageAccessibilitySubtitles.css';

export const DEFAULT_SUB_SIZE = 'sub-md';
export const DEFAULT_SUB_STYLE = 'sub-outline';

const size = [
    {
        label: 'Aa',
        description: 'SUBTITLE_TEXT_SIZE_VERY_SMALL',
        name: 'sub-xs',
        style: { fontSize: '70%', fontWeight: 700 },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_TEXT_SIZE_SMALL',
        name: 'sub-sm',
        style: { fontSize: '85%', fontWeight: 700 },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_TEXT_SIZE_REGULAR',
        name: DEFAULT_SUB_SIZE,
        style: { fontSize: '100%', fontWeight: 700 },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_TEXT_SIZE_BIG',
        name: 'sub-lg',
        style: { fontSize: '115%', fontWeight: 700 },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_TEXT_SIZE_VERY_BIG',
        name: 'sub-xl',
        style: { fontSize: '130%', fontWeight: 700 },
    },
];

const style = [
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_BLACK_OUTLINE',
        name: DEFAULT_SUB_STYLE,
        style: { fontWeight: 700, textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_DROPSHADOW',
        name: 'sub-shadow',
        style: { fontWeight: 700, textShadow: '1px 1px #000' },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_DARK_BACKGROUND',
        name: 'sub-bgdark',
        style: { fontWeight: 700, background: '#000', padding: '0 4px' },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_TRANSPARENT_DARK_BACKGROUND',
        name: 'sub-bgdark-tr',
        style: { fontWeight: 700, background: 'rgba(0, 0, 0, .4)', padding: '0 4px' },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_LIGHT_BACKGROUND',
        name: 'sub-bglight',
        style: { fontWeight: 700, background: '#fff', color: '#000', padding: '0 4px' },
    },
    {
        label: 'Aa',
        description: 'SUBTITLE_STYLE_HIGH_CONTRAST',
        name: 'sub-highcontrast',
        style: { fontWeight: 700, background: '#000', color: 'yellow', padding: '0 4px' },
    },
];

const PageAccessibility: FC = () => {
    const { setPageTitle } = useApp();
    const { isGuest } = useAuth();
    const { addToast } = useToasts();

    const sizeRef = useRef(null);
    const styleRef = useRef(null);

    const subtitleSize = guardedLocalStorage.getItem('subSize') || DEFAULT_SUB_SIZE;
    const subtitleStyle = guardedLocalStorage.getItem('subStyle') || DEFAULT_SUB_STYLE;

    const [subSize, setSubSize] = useState(subtitleSize);
    const [subStyle, setSubStyle] = useState(subtitleStyle);

    const displayToast = (title: string) => {
        addToast({ title } as unknown as React.ReactNode, {
            appearance: 'success',
            autoDismiss: true,
        });
    };

    useEffect(() => {
        setPageTitle(translate('SETTINGS_SUBTITLE_APPEARANCE'));
    }, []);

    return (
        <HelmetWrapper {...getHelmetData(translate('SETTINGS_SUBTITLE_APPEARANCE'))}>
            <PageWrapper>
                <PageHeading srOnly>{translate('SETTINGS_SUBTITLE_APPEARANCE')}</PageHeading>
                {!isGuest && (
                    <Section aria-label={translate('SETTINGS_SUBTITLE_APPEARANCE')}>
                        <Hint>{translate('SUBTITLE_APPEARANCE_HINT_TEXT')}</Hint>

                        <SubtitlePreview>
                            <SubtitlePreviewText className={`sub-preview ${subSize} ${subStyle}`}>
                                {translate('SUBTITLE_PREVIEW_TEXT')}
                            </SubtitlePreviewText>
                        </SubtitlePreview>

                        <SubSection>
                            <SectionTitle>
                                {translate('SETTINGS_SUBTITLE_TEXT_SIZE')}: {translate(size.find((o) => o.name === subSize)?.description)}
                            </SectionTitle>
                            <ButtonRadioGroup
                                label={translate('SETTINGS_SUBTITLE_TEXT_SIZE')}
                                name="subtitleSize"
                                values={size}
                                initialValue={subSize}
                                resetValue={DEFAULT_SUB_SIZE}
                                onChange={(value) => setSubSize(value)}
                                isHorizontal
                                ref={sizeRef}
                            />
                        </SubSection>

                        <SubSection>
                            <SectionTitle>
                                {translate('SETTINGS_SUBTITLE_STYLE')}: {translate(style.find((o) => o.name === subStyle)?.description)}
                            </SectionTitle>
                            <ButtonRadioGroup
                                label={translate('SETTINGS_SUBTITLE_STYLE')}
                                name="subtitleStyle"
                                values={style}
                                initialValue={subStyle}
                                resetValue={DEFAULT_SUB_STYLE}
                                onChange={(value) => setSubStyle(value)}
                                isHorizontal
                                ref={styleRef}
                            />
                        </SubSection>

                        <ButtonWrapper>
                            <Button
                                label={translate('SETTINGS_SUBTITLE_RESTORE_DEFAULT')}
                                onClick={() => {
                                    guardedLocalStorage.setItem('subSize', DEFAULT_SUB_SIZE);
                                    guardedLocalStorage.setItem('subStyle', DEFAULT_SUB_STYLE);
                                    setSubSize(DEFAULT_SUB_SIZE);
                                    setSubStyle(DEFAULT_SUB_STYLE);

                                    // @ts-ignore
                                    if (sizeRef?.current) sizeRef.current.forceReset();
                                    // @ts-ignore
                                    if (styleRef?.current) styleRef.current.forceReset();

                                    displayToast(translate('DEFAULTS_RESTORED_NOTIFICATION'));
                                }}
                            />
                            <Button
                                label={translate('SAVE_BUTTON')}
                                onClick={() => {
                                    guardedLocalStorage.setItem('subSize', subSize);
                                    guardedLocalStorage.setItem('subStyle', subStyle);

                                    displayToast(translate('CHANGES_SAVED_NOTIFICATION'));
                                }}
                                isPrimary
                            />
                        </ButtonWrapper>
                    </Section>
                )}
            </PageWrapper>
        </HelmetWrapper>
    );
};

export default PageAccessibility;
